import type {
  CreatedFields,
  ProfileFields,
  SchemaWithId,
} from '@soniq/schema'
import { EntityKeys } from '../types'
import {
  AsyncQueryResponse,
  AsyncSingleResponse,
  EntityEventsBuilder,
  EventSourcingDatabase,
  EventStore,
  NewBuilderDomainEvent,
  ProfileProjectionSelectQueryBuilder,
  ProfileProjectionState,
  ProjectionEntityStore,
  ResourceRequestOptions,
  ResponseWithRefetch,
  UpsertSchema,
} from '@soniq/public-resource'
import { useCallback } from 'react'
import { ValidateSchemaOptions } from '@pogokid/schema'
import { isArrayWithLength, Optional } from '@pogokid/util'
import { Kysely, useReactiveQuery } from '@pogokid/dblite/react'
import { useEnsureAuthUser } from '@soniq/util-auth'
import { useDb } from '../db.ts'

export function projectionEntityHooksFactory<
  Schema extends SchemaWithId<CreatedFields & ProfileFields>,
  Keys extends EntityKeys<Record<string, unknown>>,
  Table extends keyof ProfileProjectionState,
  Store extends ProjectionEntityStore<Schema>,
  Builder extends EntityEventsBuilder<Schema> = EntityEventsBuilder<Schema>
>(
  entityKeys: Keys,
  store: Store,
  builder: Builder,
  useFetchOptions: () => ResourceRequestOptions
) {
  return {
    useValidate() {
      const validate = useCallback(
        (
          data: Schema & { id?: string },
          options?: ValidateSchemaOptions
        ) => {
          return store.validate(data, options)
        },
        []
      )
      const validateForm = useCallback(
        (
          data: Schema & { id?: string },
          options?: ValidateSchemaOptions
        ) => validate(data, options).errors,
        [validate]
      )
      return { validate, validateForm }
    },
    useById(
      profileId: Optional<string>,
      id: Optional<string>
    ): AsyncSingleResponse<Schema> & ResponseWithRefetch {
      const enabled = !!profileId && !!id
      const requestOptions = useFetchOptions()
      const query = store
        .fetchByIdQuery(
          requestOptions,
          profileId ?? '',
          id ?? ''
        )
        .compile()

      const { value, ...results } = useReactiveQuery<Schema>({
        query,
        enabled,
        normaliseRecordData: (data) =>
          store.normaliseRecordData(data),
      })
      return {
        value: value?.[0] ?? null,
        ...results,
        refetch() {},
      }
    },
    useByIds(
      profileId: Optional<string>,
      ids: Optional<string[]>
    ): AsyncQueryResponse<Schema> {
      const enabled =
        !!profileId && !!isArrayWithLength(ids?.filter(Boolean))
      const requestOptions = useFetchOptions()
      const query = store
        .fetchByIdsQuery(
          requestOptions,
          profileId ?? '',
          ids ?? []
        )
        .compile()

      return useReactiveQuery({
        query,
        enabled,
        normaliseRecordData: (data) =>
          store.normaliseRecordData(data),
      })
    },
    useByQuery(
      profileId: Optional<string>,
      filters?:
        | Partial<Schema>
        | ((
            queryBuilder: ProfileProjectionSelectQueryBuilder<Table>,
            store: Store,
            profileId: Optional<string>,
            requestOptions: ResourceRequestOptions
          ) => ProfileProjectionSelectQueryBuilder<Table>),
      options?: {
        enabled: boolean
      }
    ): AsyncQueryResponse<Schema> {
      const enabled = options?.enabled !== false && !!profileId
      const requestOptions = useFetchOptions()

      let query = store.fetchQueryQuery(
        requestOptions,
        profileId ?? '',
        typeof filters === 'object' ? filters : undefined
      ) as unknown as ProfileProjectionSelectQueryBuilder<Table>

      if (enabled && typeof filters === 'function') {
        query = filters(query, store, profileId, requestOptions)
      }

      if (!enabled) {
        query = query.limit(0)
      }

      return useReactiveQuery<Schema>({
        query: query.compile(),
        enabled,
        normaliseRecordData: (data) =>
          store.normaliseRecordData(data),
      })
    },
    useMutation() {
      const authUser = useEnsureAuthUser()
      const db = useDb()
        .db as unknown as Kysely<EventSourcingDatabase>
      const requestOptions = useFetchOptions()
      return {
        create: useCallback(
          async (
            profileId: string,
            data: NewBuilderDomainEvent<Schema>['payload']
          ) => {
            const eventStore = EventStore.new(db)
            const domainEvent = builder.created({
              profile_id: profileId,
              user_id: authUser.profile.sub,
              payload: data,
            })
            await eventStore.append(domainEvent)
          },
          [authUser.profile.sub, db]
        ),
        /**
         * @deprecated use updateOrCreate for single or create for multiple
         */
        upsert: useCallback(
          async (
            profileId: string,
            data: UpsertSchema<Schema>[]
          ) => {
            const eventStore = EventStore.new(db)
            const domainEvent = builder.upserted({
              profile_id: profileId,
              user_id: authUser.profile.sub,
              payload: data,
            })
            await eventStore.append(domainEvent)
          },
          [authUser.profile.sub, db]
        ),
        update: useCallback(
          async (
            profileId: string,
            data: SchemaWithId<Partial<Schema>>
          ) => {
            const eventStore = EventStore.new(db)
            const domainEvent = builder.updated({
              profile_id: profileId,
              user_id: authUser.profile.sub,
              payload: data,
            })
            await eventStore.append(domainEvent)
          },
          [authUser.profile.sub, db]
        ),
        delete: useCallback(
          async (profileId: string, id: string) => {
            const eventStore = EventStore.new(db)
            const domainEvent = builder.deleted({
              profile_id: profileId,
              user_id: authUser.profile.sub,
              payload: { id },
            })
            await eventStore.append(domainEvent)
          },
          [authUser.profile.sub, db]
        ),
        updateOrCreate: useCallback(
          async (
            profileId: string,
            data: NewBuilderDomainEvent<Schema>['payload']
          ) => {
            const eventStore = EventStore.new(db)
            const existing = data.id
              ? (
                  await store.fetchById(
                    requestOptions,
                    profileId,
                    data.id
                  )
                ).data
              : null
            if (existing) {
              const domainEvent = builder.updated({
                profile_id: profileId,
                user_id: authUser.profile.sub,
                payload: data,
              })
              await eventStore.append(domainEvent)
            } else {
              const domainEvent = builder.created({
                profile_id: profileId,
                user_id: authUser.profile.sub,
                payload: data,
              })
              await eventStore.append(domainEvent)
            }
          },
          [authUser.profile.sub, db, requestOptions]
        ),
      }
    },
  }
}
